// Mecanismo MTF (multitimeframe) genérico para indicadores con endpoint propio.
//
// Idea: el endpoint de cada indicador ya acepta `resolution` arbitraria, así que
// "ver el indicador calculado en timeframes superiores" no requiere backend —
// basta una petición por TF adicional con los mismos parámetros de lógica y un
// merge de las respuestas etiquetando cada drawable con su TF de origen.
//
// Nació para Order Blocks Quantum v1.2 (cajas) y se extendió a CHoCH/BOS
// Quantum v1.1 (líneas/etiquetas) y FVG/IFVG Quantum v1.1 (cajas): el mecanismo
// es agnóstico al tipo de drawable — `zoneKeys` (nombre histórico) son simples
// claves del payload cuyo valor es un array de drawables a etiquetar/mergear:
// OB pasa `['obs','mitigated_obs','breakers']`, CHoCH/BOS `['lines']`, FVG
// `['fvgs','mitigated_fvgs','ifvgs']`. La distinción visual (dotted, sufijo,
// relleno atenuado) y los contadores "badge == pintadas" viven en el render de
// cada indicador, no aquí.
//
// Convención de etiquetado: los drawables del TF propio del chart NO llevan
// marca; los de TFs adicionales llevan `_mtf: '<tf>'` (valor API:
// '15m' | '1h' | '4h' | '1d').
//
// Dedup: por construcción propio y HTF no deberían solapar (sus tiempos vienen
// de rejillas de velas distintas). Si un drawable HTF y uno propio colisionan,
// el contrato es que MANDA EL PROPIO: el merge coloca primero los del TF propio
// y cada render deduplica dando preferencia a los no marcados.

// Universo de timeframes ofrecibles como "adicionales", en orden ascendente.
// Es el mismo conjunto que las suscripciones Telegram (SUPPORTED_TFS).
export const MTF_TIMEFRAMES = ['15m', '1h', '4h', '1d']

// Etiqueta de pantalla por valor API (sufijo en etiquetas: `OB+ · 4H`).
export const MTF_TF_LABELS = { '15m': '15m', '1h': '1H', '4h': '4H', '1d': '1D' }

// Máximo de TFs adicionales simultáneos (rendimiento y legibilidad).
export const MTF_MAX_ADDITIONAL = 2

// Duración aproximada en segundos por valor API de TIMEFRAMES — solo para
// ordenar ("mayor que el TF del chart"), nunca para lógica de mercado.
const TF_SECONDS = {
  '1m': 60, '5m': 300, '10m': 600, '15m': 900, '30m': 1800,
  '1h': 3600, '2h': 7200, '4h': 14400,
  '1d': 86400, '1w': 604800, '1M': 2592000,
}

export function tfSeconds(tf) {
  return TF_SECONDS[tf] ?? null
}

// TFs adicionales ofrecibles para un chart en `currentTf`: los del universo
// MTF estrictamente mayores que el actual. En el TF más alto (1D) devuelve [].
export function higherTimeframes(currentTf) {
  const cur = TF_SECONDS[currentTf]
  if (cur == null) return []
  return MTF_TIMEFRAMES.filter((tf) => (TF_SECONDS[tf] ?? 0) > cur)
}

// Filtra una selección persistida dejando solo TFs válidos para el chart actual
// (estrictamente mayores), sin duplicados y capada a `max`. Es la "limpieza de
// zonas obsoletas" al cambiar el timeframe del chart: lo que ya no es mayor
// simplemente deja de pedirse/pintarse, sin tocar la config guardada.
export function sanitizeHtfTimeframes(selected, currentTf, max = MTF_MAX_ADDITIONAL) {
  if (!Array.isArray(selected)) return []
  const allowed = new Set(higherTimeframes(currentTf))
  const out = []
  for (const tf of selected) {
    if (allowed.has(tf) && !out.includes(tf)) out.push(tf)
    if (out.length >= max) break
  }
  return out
}

export function mtfLabel(tf) {
  return MTF_TF_LABELS[tf] ?? String(tf).toUpperCase()
}

// Sufijo canónico de timeframe para etiquetas de estructura (`OB+ · 4H`).
export function mtfSuffix(tf) {
  return ` · ${mtfLabel(tf)}`
}

// Devuelve una copia de `data` con cada zona de `zoneKeys` marcada con
// `_mtf: tf`. Las zonas del TF propio nunca pasan por aquí: van sin marca.
export function tagZonesWithTimeframe(data, tf, zoneKeys) {
  if (!data || typeof data !== 'object') return data
  const out = { ...data }
  for (const key of zoneKeys) {
    if (Array.isArray(data[key])) {
      out[key] = data[key].map((z) => ({ ...z, _mtf: tf }))
    }
  }
  return out
}

// Mergea la respuesta del TF propio con las de los TFs adicionales.
//   ownData:    respuesta del endpoint para el TF del chart (sin marcar)
//   htfResults: [{ tf, data }] con las respuestas de TFs adicionales
//   zoneKeys:   claves del payload que contienen zonas (p.ej. OB:
//               ['obs', 'mitigated_obs', 'breakers'])
// El resto del payload (dashboard, timestamp…) se toma del TF propio: los
// contadores del badge siguen contando solo el TF propio.
export function mergeMtfIndicatorData(ownData, htfResults, zoneKeys) {
  if (!ownData || typeof ownData !== 'object') return ownData
  const active = (htfResults || []).filter((r) => r && r.data && r.tf)
  if (active.length === 0) return { ...ownData, _mtfTimeframes: [] }
  const merged = { ...ownData, _mtfTimeframes: active.map((r) => r.tf) }
  for (const key of zoneKeys) {
    const own = Array.isArray(ownData[key]) ? ownData[key] : []
    merged[key] = [
      ...own,
      ...active.flatMap((r) => (tagZonesWithTimeframe(r.data, r.tf, [key])[key] || [])),
    ]
  }
  return merged
}

// Orquesta el fetch MTF completo: una petición para el TF propio (sin
// `resolution` extra — `baseParams` ya la lleva) y una por cada TF adicional,
// con los mismos parámetros de lógica y `resolution` sustituida.
//   fetchFn: (params) => Promise<data>  (la página la envuelve con su
//            servicio, signal/timeout y el unwrap de `res.data ?? res`)
// Política de fallos: si falla la petición del TF propio, se relanza el error
// (la página aplica su fail-soft habitual); si falla una HTF, ese TF se omite
// y el resto se pinta igual — un TF adicional caído nunca apaga el indicador.
export async function fetchMtfIndicatorData(fetchFn, baseParams, htfTimeframes, zoneKeys) {
  const tfs = htfTimeframes || []
  const results = await Promise.allSettled([
    fetchFn(baseParams),
    ...tfs.map((tf) => fetchFn({ ...baseParams, resolution: tf })),
  ])
  if (results[0].status === 'rejected') throw results[0].reason
  const htf = []
  for (let i = 1; i < results.length; i++) {
    if (results[i].status === 'fulfilled') htf.push({ tf: tfs[i - 1], data: results[i].value })
  }
  return mergeMtfIndicatorData(results[0].value, htf, zoneKeys)
}
